import React, { useState } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Snackbar,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  CircularProgress,
} from "@mui/material";
import DeleteSweepIcon from "@mui/icons-material/DeleteSweep";
import { useTheme } from "@mui/material/styles";
import { adminSurfaceSx, adminPageSx } from "./adminStyles";
import AdminPageHeader from "./AdminPageHeader";
import { adminRequest } from "../../utils/adminApi";

// Admin purge console (Admin UI for CodeJonesW/hybra#421). Lets an admin enter a
// target user's email + a cutoff date and bulk-delete that user's workouts
// created before the date. Fires through the deleteWorkoutsBeforeDate proxy,
// which forwards the request to worker DELETE /api/admin/delete-workouts-before-date
// (admin-gated server-side). The destructive action requires an explicit confirm
// dialog that restates the email + date before firing.
export default function PurgeWorkouts() {
  const theme = useTheme();
  // env-aware: prod uses the authSlice token as today; staging uses the held
  // staging token and routes via ?env=staging (adminRequest).
  const { env, token } = useSelector(adminRequest);
  const [targetEmail, setTargetEmail] = useState("");
  const [beforeDate, setBeforeDate] = useState("");
  const [emailError, setEmailError] = useState("");
  const [dateError, setDateError] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [snack, setSnack] = useState("");

  const openConfirm = () => {
    let valid = true;
    if (!targetEmail.trim()) {
      setEmailError("Target user email is required");
      valid = false;
    } else {
      setEmailError("");
    }
    if (!beforeDate) {
      setDateError("Cutoff date is required");
      valid = false;
    } else {
      setDateError("");
    }
    if (valid) setConfirmOpen(true);
  };

  const handleConfirm = async () => {
    setConfirmOpen(false);
    if (env === "staging" && !token) {
      setSnack("Log in to staging first.");
      return;
    }
    setDeleting(true);
    try {
      // env is routing-only; the proxy strips it before forwarding.
      const envQuery = env === "staging" ? "?env=staging" : "";
      const response = await axios.post(
        `/api/admin/deleteWorkoutsBeforeDate${envQuery}`,
        { target_email: targetEmail.trim(), before_date: beforeDate },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const deleted = response.data?.deleted ?? 0;
      setSnack(
        `Deleted ${deleted} workout${deleted === 1 ? "" : "s"} for ${targetEmail.trim()} before ${beforeDate}.`
      );
    } catch (err) {
      console.error("Failed to purge workouts:", err);
      const message =
        err.response?.data?.error ||
        err.message ||
        "Failed to delete workouts.";
      setSnack(message);
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box>
      <Box sx={adminPageSx}>
        <AdminPageHeader
          icon={<DeleteSweepIcon />}
          iconAccent={theme.palette.error.main}
          title="Purge workouts"
          subtitle="Delete a user's workouts created before a cutoff date (Admin UI for hybra#421). Destructive — requires an explicit confirm."
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 8, lg: 6 }}>
            <Card sx={adminSurfaceSx(theme)}>
              <CardContent>
                <TextField
                  fullWidth
                  label="Target user email"
                  type="email"
                  value={targetEmail}
                  onChange={(e) => setTargetEmail(e.target.value)}
                  error={!!emailError}
                  helperText={emailError}
                  margin="normal"
                  slotProps={{ htmlInput: { "data-testid": "purge-email" } }}
                />
                <TextField
                  fullWidth
                  label="Delete workouts before"
                  type="date"
                  value={beforeDate}
                  onChange={(e) => setBeforeDate(e.target.value)}
                  error={!!dateError}
                  helperText={dateError}
                  margin="normal"
                  slotProps={{ inputLabel: { shrink: true }, htmlInput: { "data-testid": "purge-date" } }}
                />
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="contained"
                    color="error"
                    startIcon={
                      deleting ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DeleteSweepIcon />
                      )
                    }
                    onClick={openConfirm}
                    disabled={deleting}
                  >
                    Delete workouts
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        fullWidth
        maxWidth="sm"
        slotProps={{ paper: {
          sx: {
            borderRadius: 3,
            boxShadow: 12,
            border: `1px solid ${theme.palette.divider}`,
            background: theme.palette.background.paper,
            backgroundImage: "none",
          },
        } }}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography component="span" variant="h6" sx={{ fontWeight: 700 }}>
            Confirm bulk delete
          </Typography>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>
            This permanently deletes all workouts for{" "}
            <strong>{targetEmail.trim()}</strong> created before{" "}
            <strong>{beforeDate}</strong>. This cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3 }}>
          <Button
            onClick={() => setConfirmOpen(false)}
            color="inherit"
            sx={{ fontWeight: 500 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            color="error"
            data-testid="purge-confirm"
          >
            Delete workouts
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snack}
        autoHideDuration={5000}
        onClose={() => setSnack("")}
        message={snack}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Box>
  );
}
